import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Format cents to a dollar string.
 * @param {number} cents
 * @returns {string}
 */
function formatCents(cents) {
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

/**
 * Fetch product data from the AJAX API.
 * @param {string} handle
 * @returns {Promise<Object>}
 */
async function fetchProduct(handle) {
  const res = await fetch(`/products/${handle}.json`);
  if (!res.ok) throw new Error(`Failed to fetch product: ${handle}`);
  const data = await res.json();
  return data.product;
}

/**
 * Resize a Shopify image URL.
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
function getImageWithSize(src, width) {
  if (!src) return '';
  if (src.includes('?')) return src.replace(/&?width=\d+/, '') + `&width=${width}`;
  return src + `?width=${width}`;
}

// ─── BundleBuilderComponent ─────────────────────────────────────────
/**
 * Outermost wrapper. Manages global bundle state, handles price display
 * updates, orchestrates navigation between steps via custom events.
 *
 * @extends Component
 */
class BundleBuilderComponent extends Component {
  /** @type {Object} */
  state = {};

  /** Current step index */
  currentStep = 0;

  connectedCallback() {
    super.connectedCallback();

    // Initialize state from the server-rendered config
    this.state = window.bundleConfig || {};
    this.totalSteps = parseInt(this.dataset.totalSteps) || 0;
    this.discount = parseFloat(this.dataset.discount) || 0;

    // Listen for custom events
    document.addEventListener('bundle:navigate', this.#onNavigate);
    document.addEventListener('bundle:selection-change', this.#onSelectionChange);
    document.addEventListener('bundle:price-update', this.#onPriceUpdate);
    document.addEventListener('bundle:personalization-change', this.#onPersonalizationChange);
    document.addEventListener('bundle:addon-change', this.#onAddonChange);

    // Show first step
    this.#showStep(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('bundle:navigate', this.#onNavigate);
    document.removeEventListener('bundle:selection-change', this.#onSelectionChange);
    document.removeEventListener('bundle:price-update', this.#onPriceUpdate);
    document.removeEventListener('bundle:personalization-change', this.#onPersonalizationChange);
    document.removeEventListener('bundle:addon-change', this.#onAddonChange);
  }

  /**
   * Navigate to a specific step.
   * @param {number} stepIndex
   */
  goToStep(stepIndex) {
    if (stepIndex < 0 || stepIndex > this.totalSteps) return;
    this.#showStep(stepIndex);
  }

  #showStep(stepIndex) {
    this.currentStep = stepIndex;
    const steps = this.refs.steps;
    if (!steps) return;

    steps.forEach((step) => {
      const isActive = parseInt(step.dataset.step) === stepIndex;
      step.classList.toggle('bundle-step--active', isActive);
      step.hidden = !isActive;
    });

    // Update nav
    const nav = this.refs.nav;
    if (nav) {
      nav.setActiveStep(stepIndex);
    }

    // Scroll to top of builder
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Update price display
    this.#updatePriceDisplay();
  }

  #onNavigate = (e) => {
    const { step } = e.detail;
    this.goToStep(step);
  };

  #onSelectionChange = (e) => {
    const { step, variantId, price, compareAtPrice, productTitle, productHandle, featuredImage, groupIndex } = e.detail;
    if (this.state.selections && this.state.selections[step]) {
      this.state.selections[step].id = variantId;
      this.state.selections[step].price = price;
      this.state.selections[step].compareAtPrice = compareAtPrice;
      this.state.selections[step].productTitle = productTitle;
      this.state.selections[step].productHandle = productHandle;
      this.state.selections[step].featuredImage = featuredImage;
    }
    this.#updatePriceDisplay();
  };

  #onPriceUpdate = () => {
    this.#updatePriceDisplay();
  };

  #onPersonalizationChange = (e) => {
    const { step, selected, properties } = e.detail;
    if (this.state.selections && this.state.selections[step]) {
      this.state.selections[step].personalization.selected = selected;
      if (properties) {
        this.state.selections[step].personalization.properties = properties;
      }
    }
    this.#updatePriceDisplay();
  };

  #onAddonChange = (e) => {
    const { step, selected, id, title, handle, image, price, compareAtPrice } = e.detail;
    if (this.state.selections && this.state.selections[step]) {
      this.state.selections[step].addon.selected = selected;
      if (id) {
        Object.assign(this.state.selections[step].addon, {
          id, title, handle, image, price, compareAtPrice,
        });
      }
    }
    this.#updatePriceDisplay();
  };

  #updatePriceDisplay() {
    if (!this.state.selections) return;

    let totalPrice = 0;
    let totalCompare = 0;

    for (const sel of this.state.selections) {
      totalPrice += sel.price || 0;
      totalCompare += sel.compareAtPrice || sel.price || 0;

      if (sel.addon?.selected && sel.addon.price) {
        totalPrice += sel.addon.price;
        totalCompare += sel.addon.compareAtPrice || sel.addon.price;
      }

      if (sel.personalization?.selected && sel.personalization.price) {
        totalPrice += sel.personalization.price;
        totalCompare += sel.personalization.price;
      }

      if (sel.accidentProtection?.selectedPrice) {
        totalPrice += sel.accidentProtection.selectedPrice;
        totalCompare += sel.accidentProtection.selectedPrice;
      }
    }

    // Add upsells
    for (const upsell of (this.state.upsells || [])) {
      totalPrice += upsell.price || 0;
      totalCompare += upsell.compareAtPrice || upsell.price || 0;
    }

    // Apply bundle discount (in cents)
    const discountCents = this.discount * 100;
    const savings = (totalCompare - totalPrice) + discountCents;
    totalPrice = Math.max(0, totalPrice - discountCents);

    // Update displays
    if (this.refs.priceDisplay) {
      this.refs.priceDisplay.textContent = formatCents(totalPrice);
    }
    if (this.refs.compareDisplay) {
      this.refs.compareDisplay.textContent = formatCents(totalCompare);
      this.refs.compareDisplay.hidden = totalCompare <= totalPrice;
    }
    if (this.refs.savingsDisplay) {
      this.refs.savingsDisplay.textContent = `You save ${formatCents(savings)}`;
      this.refs.savingsDisplay.hidden = savings <= 0;
    }
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}

// ─── BundleNavComponent ─────────────────────────────────────────────
/**
 * Top navigation bar with step numbers, updates active step highlighting.
 *
 * @extends Component
 */
class BundleNavComponent extends Component {
  /**
   * Set the active step in the nav bar.
   * @param {number} stepIndex
   */
  setActiveStep(stepIndex) {
    const buttons = this.refs.navButtons;
    if (!buttons) return;

    buttons.forEach((btn) => {
      const btnStep = parseInt(btn.dataset.step);
      btn.classList.toggle('bundle-nav__btn--active', btnStep === stepIndex);
      btn.classList.toggle('bundle-nav__btn--completed', btnStep < stepIndex);
    });
  }

  /**
   * Handle click on a nav button.
   * @param {Object} data - Parsed query params ({ step: number })
   * @param {Event} event
   */
  handleNavClick(data, event) {
    const step = typeof data?.step === 'number' ? data.step : parseInt(event.target.closest('[data-step]')?.dataset.step);
    if (isNaN(step)) return;
    document.dispatchEvent(new CustomEvent('bundle:navigate', { detail: { step } }));
  }
}

if (!customElements.get('bundle-nav-component')) {
  customElements.define('bundle-nav-component', BundleNavComponent);
}

// ─── BundleStepComponent ────────────────────────────────────────────
/**
 * Individual step: swatch tab switching, swatch selection, product image
 * gallery update (via /products/{handle}.json fetch), next/prev navigation.
 *
 * @extends Component
 */
class BundleStepComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.stepIndex = parseInt(this.dataset.step);
  }

  /**
   * Handle clicking a group tab.
   * @param {Object} data - Parsed query params ({ group: number })
   * @param {Event} event
   */
  handleGroupTab(data, event) {
    const groupIndex = typeof data?.group === 'number' ? data.group : parseInt(event.target.closest('[data-group]')?.dataset.group);
    if (isNaN(groupIndex)) return;

    // Update tab active state
    const tabs = this.refs.groupTabButtons;
    if (tabs) {
      tabs.forEach((t) => t.classList.toggle('bundle-step__group-tab--active', parseInt(t.dataset.group) === groupIndex));
    }

    // Show/hide swatch groups
    const groups = this.refs.swatchGroups;
    if (groups) {
      groups.forEach((g) => {
        const isActive = parseInt(g.dataset.group) === groupIndex;
        g.classList.toggle('bundle-swatches--active', isActive);
      });
    }

    // Select first available swatch in the new group
    const activeGroup = groups?.find((g) => parseInt(g.dataset.group) === groupIndex);
    if (activeGroup) {
      const firstRadio = activeGroup.querySelector('input[type="radio"]:not(:disabled)');
      if (firstRadio) {
        firstRadio.checked = true;
        firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Handle swatch radio change.
   * @param {Event} event
   */
  async handleSwatchChange(event) {
    const input = event.target;
    if (!input) return;

    const handle = input.dataset.handle;
    const step = parseInt(input.dataset.step);
    const group = parseInt(input.dataset.group);
    const price = parseInt(input.dataset.price) || 0;
    const comparePrice = parseInt(input.dataset.priceCompare) || 0;
    const featuredImage = input.dataset.featuredImage;
    const title = input.dataset.title;

    // Update price display in step
    const priceEl = this.refs.stepPrice;
    if (priceEl) priceEl.textContent = formatCents(price);

    const comparePriceEl = this.refs.stepCompare;
    if (comparePriceEl) {
      if (comparePrice > price) {
        comparePriceEl.textContent = formatCents(comparePrice);
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.hidden = true;
      }
    }

    // Update selected product name
    const selectedNameEl = this.refs.selectedName;
    if (selectedNameEl && title) {
      selectedNameEl.textContent = title;
    }

    // Dispatch selection change event
    document.dispatchEvent(new CustomEvent('bundle:selection-change', {
      detail: {
        step,
        variantId: parseInt(input.value),
        price,
        compareAtPrice: comparePrice,
        productTitle: title,
        productHandle: handle,
        featuredImage,
        groupIndex: group,
      },
    }));

    // Fetch product data and update gallery
    try {
      const product = await fetchProduct(handle);
      this.#updateGallery(product);
    } catch (err) {
      console.error('Failed to fetch product for gallery update:', err);
    }
  }

  /**
   * Update the gallery with product images.
   * @param {Object} product
   */
  #updateGallery(product) {
    const mainImage = this.refs.mainImage;
    const thumbnails = this.refs.thumbnails;
    if (!mainImage || !product.images?.length) return;

    // Update main image to show first product image
    const firstImage = product.images[0];
    mainImage.innerHTML = `<img src="${getImageWithSize(firstImage.src, 800)}" alt="${firstImage.alt || product.title}" width="800" height="800" loading="lazy">`;

    // Build thumbnails
    if (thumbnails) {
      thumbnails.innerHTML = product.images
        .map(
          (img, i) => `
          <button class="bundle-step__thumb${i === 0 ? ' bundle-step__thumb--active' : ''}" data-index="${i}" on:click="bundle-step-component/handleThumbClick">
            <img src="${getImageWithSize(img.src, 120)}" alt="${img.alt || product.title}" width="120" height="120" loading="lazy">
          </button>
        `
        )
        .join('');
    }

    // Store images for thumbnail navigation
    this._productImages = product.images;
  }

  /**
   * Handle thumbnail click.
   * @param {Event} event
   */
  handleThumbClick(event) {
    const thumb = event.target.closest('[data-index]');
    if (!thumb) return;

    const index = parseInt(thumb.dataset.index);
    const mainImage = this.refs.mainImage;
    if (!mainImage) return;

    // Update main image
    const images = this._productImages;
    if (images && images[index]) {
      const img = images[index];
      mainImage.innerHTML = `<img src="${getImageWithSize(img.src, 800)}" alt="${img.alt || ''}" width="800" height="800" loading="lazy">`;
    }

    // Update active thumb state
    const thumbs = this.refs.thumbnails?.querySelectorAll('.bundle-step__thumb');
    thumbs?.forEach((t, i) => t.classList.toggle('bundle-step__thumb--active', i === index));
  }

  /**
   * Handle add-on checkbox change.
   * @param {Event} event
   */
  handleAddonChange(event) {
    const checkbox = event.target;
    if (!checkbox) return;

    const step = parseInt(checkbox.dataset.step);

    document.dispatchEvent(new CustomEvent('bundle:addon-change', {
      detail: {
        step,
        selected: checkbox.checked,
        id: parseInt(checkbox.dataset.addonId),
        title: checkbox.dataset.addonTitle,
        handle: checkbox.dataset.addonHandle,
        image: checkbox.dataset.addonImage,
        price: parseInt(checkbox.dataset.addonPrice) || 0,
        compareAtPrice: parseInt(checkbox.dataset.addonCompare) || 0,
      },
    }));
  }

  /**
   * Handle next button click.
   */
  handleNext() {
    document.dispatchEvent(new CustomEvent('bundle:navigate', {
      detail: { step: this.stepIndex + 1 },
    }));
  }

  /**
   * Handle prev button click.
   */
  handlePrev() {
    document.dispatchEvent(new CustomEvent('bundle:navigate', {
      detail: { step: this.stepIndex - 1 },
    }));
  }
}

if (!customElements.get('bundle-step-component')) {
  customElements.define('bundle-step-component', BundleStepComponent);
}

// ─── BundleReviewComponent ──────────────────────────────────────────
/**
 * Review/summary: draws all selections, handles "Add to Cart",
 * dispatches CartAddEvent to open Horizon's cart drawer.
 *
 * @extends Component
 */
class BundleReviewComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.stepIndex = parseInt(this.dataset.step);

    // Listen for navigation to update review display
    document.addEventListener('bundle:navigate', this.#onNavigate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('bundle:navigate', this.#onNavigate);
  }

  #onNavigate = (e) => {
    if (e.detail.step === this.stepIndex) {
      this.#drawSelections();
    }
  };

  /**
   * Update the review display with current selections.
   */
  #drawSelections() {
    const builder = this.closest('bundle-builder-component');
    if (!builder?.state?.selections) return;

    const selections = builder.state.selections;

    // Update each selection row
    selections.forEach((sel, i) => {
      const imgEl = this.querySelector(`[ref="reviewImage-${i}"] img, img[ref="reviewImage-${i}"]`);
      if (imgEl && sel.featuredImage) {
        imgEl.src = getImageWithSize(sel.featuredImage, 200);
      }

      const titleEl = this.querySelector(`[ref="reviewTitle-${i}"]`);
      if (titleEl && sel.productTitle) {
        titleEl.textContent = sel.productTitle;
      }
    });

    // Update price display
    const { priceDisplay, comparePriceDisplay, savingsDisplay } = this.#calculateTotals(builder);

    const reviewPrice = this.refs.reviewPrice;
    if (reviewPrice) reviewPrice.textContent = priceDisplay;

    const reviewCompare = this.refs.reviewComparePrice;
    if (reviewCompare) {
      reviewCompare.textContent = comparePriceDisplay;
    }

    const reviewSavings = this.refs.reviewSavings;
    if (reviewSavings) {
      reviewSavings.textContent = savingsDisplay;
    }
  }

  /**
   * Calculate totals for the review.
   * @param {BundleBuilderComponent} builder
   */
  #calculateTotals(builder) {
    let totalPrice = 0;
    let totalCompare = 0;

    for (const sel of builder.state.selections) {
      totalPrice += sel.price || 0;
      totalCompare += sel.compareAtPrice || sel.price || 0;

      if (sel.addon?.selected && sel.addon.price) {
        totalPrice += sel.addon.price;
        totalCompare += sel.addon.compareAtPrice || sel.addon.price;
      }

      if (sel.personalization?.selected && sel.personalization.price) {
        totalPrice += sel.personalization.price;
        totalCompare += sel.personalization.price;
      }
    }

    for (const upsell of (builder.state.upsells || [])) {
      totalPrice += upsell.price || 0;
      totalCompare += upsell.compareAtPrice || upsell.price || 0;
    }

    const discountCents = builder.discount * 100;
    const savings = (totalCompare - totalPrice) + discountCents;
    totalPrice = Math.max(0, totalPrice - discountCents);

    return {
      priceDisplay: formatCents(totalPrice),
      comparePriceDisplay: formatCents(totalCompare),
      savingsDisplay: savings > 0 ? `Save ${formatCents(savings)}` : '',
    };
  }

  /**
   * Handle change step button click.
   * @param {Event} event
   */
  handleChangeStep(event) {
    const btn = event.target.closest('[data-step]');
    if (!btn) return;

    const step = parseInt(btn.dataset.step);
    document.dispatchEvent(new CustomEvent('bundle:navigate', { detail: { step } }));
  }

  /**
   * Handle Add to Cart button click.
   * @param {Event} event
   */
  async handleAddToCart(event) {
    const builder = this.closest('bundle-builder-component');
    if (!builder?.state?.selections) return;

    const addToCartButton = this.refs.addToCartButton;
    if (!addToCartButton || addToCartButton.disabled) return;

    addToCartButton.disabled = true;
    addToCartButton.textContent = 'Adding...';

    const bundleId = crypto.randomUUID();
    const items = [];

    // Main product selections
    for (const sel of builder.state.selections) {
      if (!sel.id) continue;

      items.push({
        id: sel.id,
        quantity: 1,
        properties: {
          _bundle: true,
          _bundle_id: bundleId,
          _bundle_title: builder.state.title || '',
        },
      });

      // Add-on
      if (sel.addon?.selected && sel.addon.id) {
        items.push({
          id: sel.addon.id,
          quantity: 1,
          properties: {
            _bundle: true,
            _bundle_id: bundleId,
            _bundle_title: builder.state.title || '',
            _parent_id: sel.id,
          },
        });
      }

      // Personalization
      if (sel.personalization?.selected && sel.personalization.id) {
        const props = {
          _bundle: true,
          _bundle_id: bundleId,
          _bundle_title: builder.state.title || '',
          _parent_id: sel.id,
        };

        // Add personalization text properties
        if (sel.personalization.properties) {
          for (const [key, value] of Object.entries(sel.personalization.properties)) {
            if (value) props[key] = value;
          }
        }

        items.push({
          id: sel.personalization.id,
          quantity: 1,
          properties: props,
        });
      }

      // Accident protection
      if (sel.accidentProtection?.selectedId) {
        items.push({
          id: sel.accidentProtection.selectedId,
          quantity: 1,
          properties: {
            _bundle: true,
            _bundle_id: bundleId,
            _bundle_title: builder.state.title || '',
            _parent_id: sel.id,
          },
        });
      }
    }

    // Upsells
    for (const upsell of (builder.state.upsells || [])) {
      if (!upsell.id) continue;
      items.push({
        id: upsell.id,
        quantity: 1,
        properties: {
          _bundle: true,
          _bundle_id: bundleId,
          _bundle_title: builder.state.title || '',
          _upsell: true,
        },
      });
    }

    if (items.length === 0) {
      addToCartButton.disabled = false;
      addToCartButton.textContent = 'Add Bundle To Cart';
      return;
    }

    try {
      // Use Shopify's /cart/add.js with items array
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (data.status) {
        // Error
        const errorEl = this.refs.atcError;
        if (errorEl) {
          errorEl.textContent = data.description || data.message || 'Error adding to cart';
          errorEl.hidden = false;
          setTimeout(() => { errorEl.hidden = true; }, 5000);
        }
      } else {
        // Success - dispatch CartAddEvent to open cart drawer
        this.dispatchEvent(new CartAddEvent({}, 'bundle-builder', {
          source: 'bundle-builder',
          itemCount: items.length,
        }));

        // Redirect to checkout if configured
        if (builder.state.atcCheckout) {
          window.location.href = '/checkout';
        }
      }
    } catch (err) {
      console.error('Bundle add to cart error:', err);
      const errorEl = this.refs.atcError;
      if (errorEl) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        errorEl.hidden = false;
        setTimeout(() => { errorEl.hidden = true; }, 5000);
      }
    } finally {
      addToCartButton.disabled = false;
      addToCartButton.textContent = 'Add Bundle To Cart';
    }
  }
}

if (!customElements.get('bundle-review-component')) {
  customElements.define('bundle-review-component', BundleReviewComponent);
}

// ─── BundleUpsellComponent ──────────────────────────────────────────
/**
 * Upsell cards in review: variant selection, add/remove toggle.
 *
 * @extends Component
 */
class BundleUpsellComponent extends Component {
  #added = false;

  /**
   * Handle variant select change.
   * @param {Event} event
   */
  handleVariantChange(event) {
    const select = event.target;
    if (!select) return;

    const option = select.options[select.selectedIndex];
    this.dataset.variantId = select.value;
    this.dataset.price = option?.dataset.price || this.dataset.price;

    // If already added, update in state
    if (this.#added) {
      this.#updateUpsellInState();
    }
  }

  /**
   * Handle add/remove toggle button click.
   */
  handleToggle() {
    this.#added = !this.#added;

    const btn = this.refs.toggleButton;
    if (btn) {
      btn.textContent = this.#added ? 'Remove' : 'Add';
      btn.classList.toggle('bundle-upsell-card__toggle--added', this.#added);
    }

    this.#updateUpsellInState();
  }

  #updateUpsellInState() {
    const builder = this.closest('bundle-builder-component');
    if (!builder) return;

    const handle = this.dataset.productHandle;
    const variantId = parseInt(this.dataset.variantId);
    const price = parseInt(this.dataset.price) || 0;
    const title = this.dataset.title;
    const image = this.dataset.image;

    if (!builder.state.upsells) builder.state.upsells = [];

    // Remove existing entry for this product
    builder.state.upsells = builder.state.upsells.filter((u) => u.handle !== handle);

    // Add back if toggled on
    if (this.#added) {
      builder.state.upsells.push({
        id: variantId,
        handle,
        title,
        image,
        price,
        compareAtPrice: price,
      });
    }

    document.dispatchEvent(new CustomEvent('bundle:price-update'));
  }
}

if (!customElements.get('bundle-upsell-component')) {
  customElements.define('bundle-upsell-component', BundleUpsellComponent);
}

// ─── BundlePersonalizationComponent ─────────────────────────────────
/**
 * Embroidery form: radio toggle, text inputs (max chars), thread color,
 * variant pricing.
 *
 * @extends Component
 */
class BundlePersonalizationComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.stepIndex = parseInt(this.dataset.step);
    this.maxChars = parseInt(this.dataset.maxChars) || 10;
  }

  /**
   * Handle yes/no toggle.
   * @param {Event} event
   */
  handleToggle(event) {
    const input = event.target;
    if (!input) return;

    const selected = input.value === 'yes';
    const wrapper = this.refs.inputsWrapper;

    if (wrapper) {
      wrapper.hidden = !selected;
    }

    document.dispatchEvent(new CustomEvent('bundle:personalization-change', {
      detail: {
        step: this.stepIndex,
        selected,
        properties: selected ? this.#getProperties() : {},
      },
    }));
  }

  /**
   * Handle text/select input change.
   * @param {Event} event
   */
  handleInputChange(event) {
    const input = event.target;
    if (!input) return;

    // Update character count if text input
    if (input.type === 'text') {
      const charCountEl = input.closest('.bundle-personalization__field')?.querySelector('[ref^="charCount"]');
      if (charCountEl) {
        charCountEl.textContent = `(${input.value.length}/${this.maxChars})`;
      }
    }

    document.dispatchEvent(new CustomEvent('bundle:personalization-change', {
      detail: {
        step: this.stepIndex,
        selected: true,
        properties: this.#getProperties(),
      },
    }));
  }

  /**
   * Get all personalization property values.
   * @returns {Object}
   */
  #getProperties() {
    const props = {};
    const inputs = this.refs.personalizationInputs;
    if (!inputs) return props;

    inputs.forEach((input) => {
      const propName = input.dataset.property;
      if (propName && input.value) {
        props[propName] = input.value;
      }
    });

    return props;
  }
}

if (!customElements.get('bundle-personalization-component')) {
  customElements.define('bundle-personalization-component', BundlePersonalizationComponent);
}
